import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';

import { IExpenseListRow } from 'core/hooks';
import { ExpenseSource } from 'core/graphql';
import { MoneyCell } from 'molecules';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';

const LinkButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

interface IExpensesTableProps {
  rows: IExpenseListRow[];
  onRowClick: (id: string) => void;
}

/** Expenses register table — columns from TZ §3.5. */
export const ExpensesTable = memo(
  ({ rows, onRowClick }: IExpensesTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const openInvoice = useCallback(
      (e: React.MouseEvent<HTMLElement>, invoiceId: string) => {
        e.stopPropagation();
        history.push(PATHS.financesInvoice.replace(':id', invoiceId));
      },
      [history],
    );

    return (
      <S.Table>
        <thead>
          <tr>
            <th>{t('pages.fakturownia.expenses.columns.date')}</th>
            <th style={{ textAlign: 'right' }}>
              {t('pages.fakturownia.expenses.columns.amount')}
            </th>
            <th>{t('pages.fakturownia.expenses.columns.category')}</th>
            <th>{t('pages.fakturownia.expenses.columns.counterparty')}</th>
            <th>{t('pages.fakturownia.expenses.columns.group')}</th>
            <th>{t('pages.fakturownia.expenses.columns.teacher')}</th>
            <th>{t('pages.fakturownia.expenses.columns.source')}</th>
            <th>{t('pages.fakturownia.expenses.columns.document')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isFakturownia = row.source === ExpenseSource.Fakturownia;
            const isRefund = (row.amount ?? 0) < 0;
            const teacherName = row.relatedTeacher
              ? [
                  row.relatedTeacher.user?.firstName,
                  row.relatedTeacher.user?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
              : '';
            return (
              <tr
                key={row.id}
                onClick={() => onRowClick(row.id)}
                style={isRefund ? { background: '#fafafa' } : undefined}
              >
                <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  {row.date ? moment(row.date).format('DD.MM.YYYY') : '—'}
                  {row.paidAt && row.paidAt !== row.date ? (
                    <div
                      style={{ fontSize: 11, color: '#94a3b8' }}
                      title={t('pages.fakturownia.expenseForm.fields.paidAt')}
                    >
                      → {moment(row.paidAt).format('DD.MM.YYYY')}
                    </div>
                  ) : null}
                </td>
                <td>
                  <MoneyCell
                    amount={row.amount}
                    currency={row.currency}
                    negative={isRefund}
                  />
                  {isRefund ? (
                    <div style={{ textAlign: 'right' }}>
                      <span
                        style={{
                          fontSize: 10,
                          color: '#94a3b8',
                          textTransform: 'uppercase',
                        }}
                      >
                        ↩ {t('pages.fakturownia.expenses.refundTag')}
                      </span>
                    </div>
                  ) : null}
                </td>
                <td>{row.category?.name || '—'}</td>
                <td>{row.counterpartyName || '—'}</td>
                <td>
                  {row.relatedGroup ? (
                    <LinkButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(
                          PATHS.groupDetail.replace(
                            ':id',
                            row.relatedGroup!.id,
                          ),
                        );
                      }}
                    >
                      {row.relatedGroup.name}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {row.relatedTeacher ? (
                    <LinkButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(
                          PATHS.teacherDetail.replace(
                            ':id',
                            row.relatedTeacher!.id,
                          ),
                        );
                      }}
                    >
                      {teacherName || '—'}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ fontSize: 11, color: '#64748b' }}>
                  {isFakturownia ? 'Fakturownia' : 'Manual'}
                </td>
                <td>
                  {row.documentUrl ? (
                    <a
                      href={row.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2563eb', fontSize: 12 }}
                    >
                      📎 {t('pages.fakturownia.expenses.openDocument')}
                    </a>
                  ) : row.relatedFakturowniaInvoice ? (
                    <LinkButton
                      type="button"
                      onClick={(e) =>
                        openInvoice(e, row.relatedFakturowniaInvoice!.id)
                      }
                    >
                      {t('pages.fakturownia.expenses.openInvoice', {
                        number: row.relatedFakturowniaInvoice.number,
                      })}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </S.Table>
    );
  },
);
